import { SubscriptionStatusResponse } from './watercraftSlice';

export const DEFAULT_POLL_DELAYS_MS = [1000, 2000, 3000, 4000, 5000];

interface ResolveCheckoutAccessDeps {
  sessionId: string | null;
  verifyCheckout: (
    checkoutSessionId: string
  ) => Promise<SubscriptionStatusResponse>;
  getSubscriptionStatus: () => Promise<SubscriptionStatusResponse>;
  pollDelaysMs?: number[];
  isCancelled?: () => boolean;
  wait?: (ms: number) => Promise<void>;
}

const defaultWait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves whether a completed checkout has granted access. The Stripe webhook is
 * the source of truth, so `verify` is only an optimistic first sync: if it 404s or
 * the webhook hasn't landed yet (has_access still false), fall back to polling the
 * status endpoint a few times with backoff before treating it as a failure.
 */
export async function verifyStripePurchase({
  sessionId,
  verifyCheckout,
  getSubscriptionStatus,
  pollDelaysMs = DEFAULT_POLL_DELAYS_MS,
  isCancelled = () => false,
  wait = defaultWait,
}: ResolveCheckoutAccessDeps): Promise<boolean> {
  if (sessionId) {
    try {
      const verified = await verifyCheckout(sessionId);
      // Verification returns successfully
      if (verified?.has_access) {
        return true;
      }
    } catch {
      /* Fall back to polling the status endpoint. */
    }
  }

  // Verification raised an error or returned a failure (has_access = false) -- Retry via GET subscription status API
  for (const delay of pollDelaysMs) {
    if (isCancelled()) {
      // Polling has been interrupted (e.g. component unmount)
      return false;
    }
    await wait(delay);
    if (isCancelled()) {
      // Check again after delay
      return false;
    }
    try {
      // Query BE for subscription status directly (outside verification endpoint)
      const status = await getSubscriptionStatus();
      if (status?.has_access) {
        return true;
      }
    } catch {
      /* Keep polling — the webhook may not have landed yet. */
    }
  }

  return false;
}
